import React, { useState, useCallback, useRef, useEffect } from "react";
import { UserPlus, RefreshCw, Mail, User as UserIcon } from "lucide-react";
import type { User, CreateAccountData } from "../types";
import "@/styles/UserManagement.css";
import { useUserManagement } from "@/hooks/useUserManagement";
import { CreateAccountModal } from "@/components/User_Management/CreateAccountModal";
import { PasswordResetModal } from "@/components/User_Management/PasswordResetModal";
import RoleModal, { Role } from "@/components/RoleModal";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";

/**
 * UserManagementPage - Main interface for user management
 * Following SOLID principles with modular design and clear separation of concerns
 */
export const UserManagementPage: React.FC = () => {
  const {
    users,
    isLoading,
    error,
    createAccount,
    resetPassword,
    refreshUsers,
  } = useUserManagement();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedUserForReset, setSelectedUserForReset] = useState<User | null>(
    null
  );

  const { getApi } = useGeneralApiCall();

  // Use ref to access latest createAccount function without causing re-renders
  const createAccountRef = useRef(createAccount);

  // Update ref when createAccount changes
  useEffect(() => {
    createAccountRef.current = createAccount;
  }, [createAccount]);

  useEffect(() => {
    getApi("api/auth/v1/roles/").then((res) => {
      setRoles(Array.isArray(res?.data) ? res.data : []);
    });
  }, [setRoles]);

  const handleCreateAccount = useCallback(
    async (accountData: CreateAccountData) => {
      console.log(
        "UserManagementPage: handleCreateAccount called with:",
        accountData
      );
      const result = await createAccountRef.current(accountData);
      console.log("UserManagementPage: createAccount returned:", result);
      if (result) {
        return result;
      }
      return null;
    },
    []
  ); // No dependencies - stable reference

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handlePasswordReset = useCallback(
    async (userId: string) => {
      const result = await resetPassword(userId);
      return result;
    },
    [resetPassword]
  );

  const openResetModal = useCallback((user: User) => {
    setSelectedUserForReset(user);
  }, []);

  const closeResetModal = useCallback(() => {
    setSelectedUserForReset(null);
  }, []);

  if (isLoading) {
    return (
      <div className="user-management-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <div className="page-header">
        <h1>User Management</h1>
        <div className="header-stats">
          <span className="stat">
            Total Users: <strong>{users.length}</strong>
          </span>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button onClick={refreshUsers} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className="user-controls">
        <div className="control-group">
          <button
            onClick={() => setShowCreateModal(true)}
            className="create-user-button"
          >
            <UserPlus size={16} />
            Create New Account
          </button>
          <button
            onClick={() => setShowRoleModal(true)}
            className="manage-roles-button"
          >
            Manage Roles
          </button>
          <button onClick={refreshUsers} className="refresh-button">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        {users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No Users Found</h3>
            <p>Start by creating your first user account.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="create-first-user-button"
            >
              Create First User
            </button>
          </div>
        ) : (
          <div className="users-table">
            <div className="table-header">
              <div className="header-cell user-name">Name</div>
              <div className="header-cell user-email">Email</div>
              <div className="header-cell user-actions">Actions</div>
            </div>

            <div className="table-body">
              {users.map((user: User) => (
                <div key={user.user_id} className="table-row">
                  <div className="table-cell user-name">
                    <div className="user-name-display">
                      <UserIcon size={16} />
                      <span>{user.name}</span>
                    </div>
                  </div>

                  <div className="table-cell user-email">
                    <div className="user-email-display">
                      <Mail size={14} />
                      <span>{user.email}</span>
                    </div>
                  </div>

                  <div className="table-cell user-actions">
                    <button
                      onClick={() => openResetModal(user)}
                      className="reset-password-button"
                      title="Reset Password"
                    >
                      {/* <Key size={16} /> */}
                      Reset Password
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Account Modal */}
      <CreateAccountModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        onCreateAccount={handleCreateAccount}
      />

      <RoleModal
        show={showRoleModal}
        onHide={() => setShowRoleModal(false)}
        roles={roles}
        setRoleModalShow={setShowRoleModal}
        setRoles={setRoles}
      />

      {/* Password Reset Modal */}
      {selectedUserForReset && (
        <PasswordResetModal
          user={selectedUserForReset}
          isOpen={true}
          onClose={closeResetModal}
          onResetPassword={handlePasswordReset}
        />
      )}
    </div>
  );
};
